import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MessageSquare, Send, X } from "lucide-react";

/**
 * Portal-mounted DM thread drawer shared by Theater Group + Theater Hall.
 * `booking` shape matches /api/theater-bookings/{id}. `myRole` is
 * either "group" or "hall" — drives bubble alignment.
 */
export default function TheaterBookingThreadDrawer({
  booking, myRole, open, onClose, onSent,
}) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/theater-bookings/${booking.id}/messages`);
      setMsgs(data.messages || []);
    } catch {
      setMsgs([]);
    } finally {
      if (!silent) setLoading(false);
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      });
    }
  };

  useEffect(() => {
    if (open && booking?.id) load();
     
  }, [open, booking?.id]);

  if (!open || !booking) return null;

  const send = async () => {
    const b = body.trim();
    if (!b) return;
    setBusy(true);
    try {
      await api.post(`/theater-bookings/${booking.id}/messages`, { body: b });
      setBody("");
      await load(true);
      onSent && onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Message failed to send");
    } finally {
      setBusy(false);
    }
  };

  const otherSide = myRole === "group" ? booking.hall_name : booking.group_name;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center sm:justify-end bg-black/60"
         onClick={onClose}
         data-testid="theater-booking-thread-drawer">
      <div className="w-full sm:w-[440px] sm:h-full h-[80vh] bg-[#0a0a0d] border-l border-white/12 flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-white/8">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
              <MessageSquare size={11} className="inline mr-1" /> Booking Thread
            </div>
            <div className="mt-1 font-display text-lg uppercase tracking-tight" data-testid="theater-thread-title">
              {otherSide || "Booking"}
            </div>
            <div className="mt-0.5 text-[11px] text-white/50">
              {booking.production_title || "—"} · {booking.requested_date} · {booking.slot}
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1"
                  data-testid="theater-thread-close">
            <X size={18} />
          </button>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3"
             data-testid="theater-thread-list">
          {loading ? (
            <div className="text-center text-white/50 py-8"><Loader2 size={14} className="animate-spin inline mr-2" /> loading…</div>
          ) : msgs.length === 0 ? (
            <div className="text-center text-white/45 text-sm py-8"
                 data-testid="theater-thread-empty">
              No messages yet — start the conversation.
            </div>
          ) : msgs.map((m, i) => {
            const mine = m.from_role === myRole;
            return (
              <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}
                   data-testid={`theater-thread-msg-${i}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${mine
                    ? "bg-[#F97316] text-black rounded-br-md"
                    : "bg-white/8 text-white rounded-bl-md"}`}>
                  {!mine && (
                    <div className="text-[9px] tracking-[0.2em] uppercase opacity-70 mb-0.5">
                      {m.from_name || m.from_role}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap">{m.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-white/8 p-3 flex items-end gap-2">
          <textarea rows={1} value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a message…"
            className="flex-1 bg-black/40 border border-white/10 rounded-lg p-2 text-sm resize-none text-white outline-none focus:border-white/30"
            data-testid="theater-thread-composer" />
          <button onClick={send} disabled={busy || !body.trim()}
            className="h-10 w-10 rounded-full bg-[#F97316] text-black flex items-center justify-center disabled:opacity-40"
            data-testid="theater-thread-send">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
